import {
  Box,
  Heading,
  HeadingLevel,
  Hide,
  HStack,
  Icon,
  Integer,
  Link,
  LinkProps,
  Show,
  Spinner,
  Text,
  TextSize,
  ThemeColorPath,
  ThemeIconKey,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {ComponentProps, ReactElement} from 'react';
import {Trans} from 'react-i18next';

import {isNotNil, map} from 'ramda';

import {isNotNilOrEmpty, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ButtonProps} from '../Button/Button';
import {ButtonGroup} from '../ButtonGroup/ButtonGroup';
import {IconButton} from '../IconButton/IconButton';

export type Hyperlink = {title: string} & Omit<LinkProps, 'children'>;

export type AlertTransMessage = {key: string} & Pick<
  ComponentProps<typeof Trans>,
  'values' | 'components'
>;

export type AlertVariants = 'info' | 'success' | 'warning' | 'error';
export type AlertTypes = 'banner' | 'inline';

export type AlertBaseProps = TestIdProps & {
  type?: AlertTypes;
  variant?: AlertVariants;
  title?: string;
  titleSize?: HeadingLevel;
  message?: Nullish | string | string[];
  messageSize?: TextSize | ValueByDevice<TextSize>;
  hyperlinks?: Hyperlink[];
  onClose?: () => void;
};

export type AlertDefaultProps = AlertBaseProps & {
  layout?: 'default';
  isLoading?: never;
  actions?: never;
  secondaryMessage?: never;
};

export type AlertDetailedProps = Omit<AlertBaseProps, 'message'> & {
  layout: 'detailed';
  message?: Nullish | string | string[] | AlertTransMessage;
  isLoading?: boolean;
  actions?: ButtonProps[];
  secondaryMessage?: string;
};

export type AlertProps = AlertDefaultProps | AlertDetailedProps;

const DEFAULT_TITLE_SIZE = 5;

export function Alert(props: AlertProps) {
  const layout = props.layout ?? 'default';
  const hasDefaultLayout = layout === 'default';
  const hasDetailedLayout = layout === 'detailed';

  const icon = match<AlertVariants, ThemeIconKey>(props.variant ?? 'warning')
    .with('info', () => 'action/info')
    .with('warning', () => 'alert/warning')
    .with('success', () => 'action/check_circle')
    .with('error', () => 'alert/error')
    .exhaustive();

  const backgroundColor = match<AlertVariants, ThemeColorPath>(props.variant ?? 'warning')
    .with('info', () => 'palettes.blue.10.100')
    .with('warning', () => 'palettes.orange.10.100')
    .with('success', () => 'palettes.green.10.100')
    .with('error', () => 'palettes.red.10.100')
    .exhaustive();

  const borderColor = match<AlertVariants, ThemeColorPath>(props.variant ?? 'warning')
    .with('info', () => 'palettes.blue.30.100')
    .with('warning', () => 'palettes.orange.30.100')
    .with('success', () => 'palettes.green.30.100')
    .with('error', () => 'palettes.red.30.100')
    .exhaustive();

  const severityColor = match<AlertVariants, ThemeColorPath>(props.variant ?? 'warning')
    .with('info', () => 'severity.informational')
    .with('warning', () => 'severity.warning')
    .with('success', () => 'severity.success')
    .with('error', () => 'severity.danger')
    .exhaustive();

  const message = match(props.message)
    .with(Pattern.array(Pattern.string), getMultilineMessage)
    .with({key: Pattern.string}, (transMessage) => (
      <Trans
        i18nKey={transMessage.key}
        values={transMessage.values}
        components={transMessage.components}
      />
    ))
    .otherwise((value) => value);

  const border = match<AlertTypes, string>(props.type ?? 'banner')
    .with('banner', () => '1px solid')
    .with('inline', () => 'none')
    .exhaustive();

  const borderLeft = match<AlertTypes, string>(props.type ?? 'banner')
    .with('banner', () => '3px solid')
    .with('inline', () => 'none')
    .exhaustive();

  const padding = match<AlertTypes, Integer>(props.type ?? 'banner')
    .with('banner', () => 3)
    .with('inline', () => 2)
    .exhaustive();

  return (
    <Box
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderTop={border}
      borderRight={border}
      borderBottom={border}
      role="alert"
      data-testid={suffixTestId('alertWrapper', props)}
    >
      <Box padding={padding} borderColor={severityColor} borderLeft={borderLeft}>
        <HStack spacing={3} justify="space-between">
          <Show when={hasDefaultLayout}>
            <HStack spacing={3} align="center">
              <Show when={isNotNil(icon)}>
                <Icon
                  value={icon}
                  size={5}
                  color={severityColor}
                  data-testid={suffixTestId('alertIcon', props)}
                />
              </Show>
              <HStack spacing={1} align="center" wrap>
                <Show when={isNotNil(props.title)}>
                  <Heading
                    size={props.titleSize ?? DEFAULT_TITLE_SIZE}
                    data-testid={suffixTestId('alertTitle', props)}
                  >
                    {props.title}
                  </Heading>
                </Show>
                <Show when={isNotNil(props.message)}>
                  <Text
                    size={props.messageSize ?? 'small'}
                    data-testid={suffixTestId('alertDescription', props)}
                  >
                    {message}
                  </Text>
                </Show>
              </HStack>
            </HStack>
            <HStack spacing={3} align="center">
              <Show when={isNotNil(props.hyperlinks)}>
                {props.hyperlinks?.map((hyperlink) => (
                  <Link
                    size="small"
                    key={hyperlink.title}
                    data-testid={suffixTestId('alertHyperlink', props)}
                    {...hyperlink}
                  />
                ))}
              </Show>
              <Show when={isNotNil(props.onClose)}>
                <IconButton
                  data-testid={suffixTestId('alertCloseButton', props)}
                  size="small"
                  onClick={props.onClose}
                  icon="navigation/close"
                />
              </Show>
            </HStack>
          </Show>

          <Show when={hasDetailedLayout}>
            <HStack spacing={3}>
              <Hide when={props.isLoading}>
                <Icon
                  value={icon}
                  size={5}
                  color={severityColor}
                  data-testid={suffixTestId('alertIcon', props)}
                />
              </Hide>
              <Show when={props.isLoading}>
                <Spinner size="small" />
              </Show>
              <VStack spacing={3} grow={1}>
                <HStack spacing={3} align="center" justify="space-between">
                  <VStack spacing={1}>
                    <Show when={isNotNil(props.title)}>
                      <Heading size={5} data-testid={suffixTestId('alertTitle', props)}>
                        {props.title}
                      </Heading>
                    </Show>
                    <Show when={isNotNil(props.message)}>
                      <Text size="small" data-testid={suffixTestId('alertDescription', props)}>
                        {message}
                      </Text>
                    </Show>
                    <Show when={props.secondaryMessage}>
                      <Text
                        size="xSmall"
                        color="secondary"
                        data-testid={suffixTestId('alertCreatedAt', props)}
                      >
                        {props.secondaryMessage}
                      </Text>
                    </Show>
                  </VStack>
                </HStack>

                <Show when={isNotNilOrEmpty(props.actions)}>
                  <ButtonGroup buttons={props.actions} />
                </Show>
              </VStack>
            </HStack>
            <HStack>
              <Show when={isNotNil(props.onClose)}>
                <IconButton
                  data-testid={suffixTestId('alertCloseButton', props)}
                  size="small"
                  onClick={props.onClose}
                  icon="navigation/close"
                />
              </Show>
            </HStack>
          </Show>
        </HStack>
      </Box>
    </Box>
  );
}

const getMultilineMessage = map<string, ReactElement>((line) => <div key={line}>{line}</div>);
